import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { PostEntity } from "@/mk2/apps/forum/schemas";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { astToHtml, defaultFilterAdditionalData } from "@/mk2/helpers/article_ast/filters";
import { dateLocal } from "@/mk2/helpers/timeformat";
import { interpolate } from "@/mk2/services/i18n";
import {
    FORUM_AI_SUMMARY_BADGE,
    FORUM_AI_SUMMARY_COMMENTS_count,
    FORUM_AI_SUMMARY_DISCLAIMER,
    FORUM_AI_SUMMARY_HIDE,
    FORUM_AI_SUMMARY_SHOW,
} from "@/mk/autogenerated/translation";

import styles from "./AIThreadSummaryCard.mscss";

interface OwnProps {
    post: PostEntity;
}

interface State {
    isExpanded: boolean;
}

export default class AIThreadSummaryCard extends React.Component<OwnProps, State> {
    public state: State = { isExpanded: false };

    private toggle = () => {
        this.setState((s) => ({ isExpanded: !s.isExpanded }));
    };

    private handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.toggle();
        }
    };

    public render() {
        const { post } = this.props;
        if (!post.aiSummaryAST) {
            return null;
        }

        const { isExpanded } = this.state;

        const additionalData = {
            ...defaultFilterAdditionalData,
            renderSnippetObjectsAs: RenderSnippetAs.HTML_IFRAME,
            renderSpecialWikiLinks: false,
            linkedObjects: post.aiSummaryASTAdditionalData?.linkedObjects || {},
            photos: [],
            rootDiv: false,
            hashizeFn: (chunk) => chunk,
            customStyles: {
                ...defaultFilterAdditionalData.customStyles,
                header2: styles.AIThreadSummaryCard__heading,
            },
        };

        return (
            <div
                className={cx(
                    styles.AIThreadSummaryCard,
                    isExpanded && styles["AIThreadSummaryCard--expanded"],
                )}
            >
                <div className={styles.AIThreadSummaryCard__header}>
                    <span className={styles.AIThreadSummaryCard__badge}>
                        {FORUM_AI_SUMMARY_BADGE}
                    </span>
                </div>
                <div
                    className={cx(
                        styles.AIThreadSummaryCard__body,
                        !isExpanded && styles["AIThreadSummaryCard__body--collapsed"],
                    )}
                >
                    {astToHtml(post.aiSummaryAST, additionalData)}
                </div>
                {isExpanded && (
                    <div className={styles.AIThreadSummaryCard__footer}>
                        {FORUM_AI_SUMMARY_DISCLAIMER}
                        {post.aiSummaryUpdatedAt && (
                            <> &middot; {dateLocal(post.aiSummaryUpdatedAt)}</>
                        )}{" "}
                        &middot;{" "}
                        {interpolate(FORUM_AI_SUMMARY_COMMENTS_count, {
                            count: post.comments,
                        })}
                    </div>
                )}
                <div
                    className={styles.AIThreadSummaryCard__bar}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onClick={this.toggle}
                    onKeyDown={this.handleKeyDown}
                >
                    <span className={styles.AIThreadSummaryCard__hint}>
                        {isExpanded ? FORUM_AI_SUMMARY_HIDE : FORUM_AI_SUMMARY_SHOW}
                    </span>
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className={styles.AIThreadSummaryCard__chevron}
                    />
                </div>
            </div>
        );
    }
}
